import * as React from "react";
import { styled, css } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { useImageProps } from "../../hooks/useImageProps.js";
import { mobileBreakPoint, mobileBreakPointCSS } from "../../defaults/theme.js";
import { cssSpacing } from "../../util/styles.js";
import {
  Constrainer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { SWIPE_IMAGE } from "../../util/constants.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

export interface TabNavTabProps {
  label?: string;
  text?: string;
  tabImage?: Image;
  image?: Image;
  noBorderRadius?: boolean;
  active?: boolean;
}

export interface TabNavProps {
  title?: string;
  tabs?: TabNavTabProps[];
}

export const TabNav: React.FC<TabNavProps> = ({ title, tabs = [] }) => {
  const navPanel = React.useRef<HTMLDivElement>(null);
  const [selectedTabIndex, setSelectedTabIndex] = React.useState<number>(0);
  const activeIndex = React.useRef<number>(0);
  const scrollEnd = React.useRef<number>();

  const applyHeight = React.useCallback(() => {
    const panel = navPanel.current;
    const slide = panel?.children[activeIndex.current] as
      | HTMLElement
      | undefined;

    if (!panel) {
      return;
    }

    panel.style.height =
      slide && window.innerWidth < mobileBreakPoint
        ? `${slide.getBoundingClientRect().height}px`
        : "";
  }, []);

  const selectTab = React.useCallback(
    (index: number) => {
      activeIndex.current = index;
      setSelectedTabIndex(index);
      applyHeight();
    },
    [applyHeight]
  );

  const handleSelectTab = React.useCallback(
    (index: number) => () => {
      if (navPanel.current) {
        navPanel.current.children[index].scrollIntoView({
          block: "nearest",
        });
        selectTab(index);
      }
    },
    [selectTab]
  );

  const handleScroll = React.useCallback(() => {
    window.clearTimeout(scrollEnd.current);

    scrollEnd.current = window.setTimeout(() => {
      const panel = navPanel.current;

      if (!panel) {
        return;
      }

      const center = panel.scrollLeft + panel.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      Array.from(panel.children).forEach((child, index) => {
        const slide = child as HTMLElement;
        const distance = Math.abs(
          slide.offsetLeft + slide.offsetWidth / 2 - center
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex.current) {
        selectTab(closestIndex);
      }
    }, 120);
  }, [selectTab]);

  React.useEffect(() => {
    const panel = navPanel.current;

    if (!panel) {
      return;
    }

    applyHeight();

    const observer = new ResizeObserver(applyHeight);
    Array.from(panel.children).forEach((slide) => observer.observe(slide));

    let lastWidth = window.innerWidth;
    const handleResize = () => {
      if (window.innerWidth !== lastWidth) {
        lastWidth = window.innerWidth;
        applyHeight();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(scrollEnd.current);
    };
  }, [applyHeight, tabs.length]);

  return (
    <VerticalSpacer>
      <Constrainer>
        <TabNavWrapper>
          {title && <h3 {...createEditableProps("text", "title")}>{title}</h3>}
          {tabs.length > 1 && (
            <Swipe>
              <img src={SWIPE_IMAGE} alt="Swipe for å se mer innhold" />
            </Swipe>
          )}
          <TabNavTabs $rowsCount={tabs.length}>
            {tabs.map((tab, index) => {
              const active = index === selectedTabIndex;

              return (
                <TabNavButton
                  className="h5"
                  key={index}
                  role="tab"
                  aria-selected={active}
                  aria-controls={`panel-${index}`}
                  onClick={handleSelectTab(index)}
                  $active={active}
                >
                  {tab.tabImage ? (
                    <TabNavnButtonImage image={tab.tabImage} />
                  ) : null}
                  {tab.label}
                </TabNavButton>
              );
            })}
          </TabNavTabs>
          <TabNavPanel
            role="tab"
            id={`panel-${selectedTabIndex}`}
            ref={navPanel}
            onScroll={handleScroll}
          >
            {tabs.map((tab, i) => (
              <Tab key={i} index={i} {...tab} active={selectedTabIndex === i} />
            ))}
          </TabNavPanel>
        </TabNavWrapper>
      </Constrainer>
    </VerticalSpacer>
  );
};

const Tab: React.FC<TabNavTabProps & { index: number }> = ({
  text,
  image,
  index,
  label,
  tabImage,
  noBorderRadius,
  active,
}) => {
  const imgProps = useImageProps(image, {
    width: 720,
    fit: "cover",
  });

  return (
    <TabNavStyledTab $active={active || false} noBorderRadius={noBorderRadius}>
      <TabNavButtonMobile as="label" $active>
        {tabImage ? <TabNavnButtonImage image={tabImage} /> : null}
        {label}
      </TabNavButtonMobile>
      {imgProps ? (
        <div className="img">
          <img {...imgProps} loading={index ? "lazy" : undefined} />
        </div>
      ) : null}
      {text ? (
        <RichText
          dangerouslySetInnerHTML={{ __html: text }}
          {...createEditableProps("html", `tabs.${index}.text`)}
        />
      ) : null}{" "}
    </TabNavStyledTab>
  );
};

function TabNavnButtonImage({ image }: { image: Image }) {
  const imgProps = useImageProps(image, {
    height: 80,
  });

  return <img {...imgProps} />;
}

export const TabNavWrapper = styled.div`
  position: relative;

  h3 {
    text-align: center;
    margin-bottom: ${cssSpacing("l")};
  }
`;

const MAX_TAB_WIDTH = 220;

const fiveRows = css`
  max-width: calc(${MAX_TAB_WIDTH + 24}px * 3);
`;
const oddRows = css`
  max-width: calc(${MAX_TAB_WIDTH + 24}px * 4);
`;

const TabNavTabs = styled.div<{ $rowsCount: number }>`
  display: none;
  justify-content: flex-start;
  gap: ${(props) => props.theme.spacing.l};
  flex-wrap: wrap;

  @media (min-width: ${mobileBreakPointCSS}) {
    display: flex;
    margin: 0 auto;
    gap: ${(props) => props.theme.spacing.l} ${(props) => props.theme.spacing.m};
    justify-content: center;

    ${(props) => {
      if (props.$rowsCount < 5) {
        return 0;
      } else if ([5, 9].includes(props.$rowsCount)) {
        return fiveRows;
      }
      return oddRows;
    }};
  }
`;

const TabNavButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  row-gap: ${(props) => props.theme.spacing.s};
  background: transparent;
  border: 0;
  border-bottom: 2px solid var(--swatch2);
  width: calc(50% - ${(props) => props.theme.spacing.m});
  padding: 0 0 ${(props) => props.theme.spacing.xs} 0;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out, opacity 0.2s ease-in-out,
    transform 0.2s ease-in-out;
  opacity: 0.6;

  ${(props) =>
    props.$active &&
    css`
      opacity: 1;
      font-weight: bold;
      border-color: var(--swatch1);
    `};

  > img {
    height: 70px;
    width: auto;
    transition: transform 0.2s ease-in-out;
  }

  :hover {
    opacity: 1;
    border-color: var(--swatch1);
    transform: scale(1.05);
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    width: ${MAX_TAB_WIDTH}px;
  }
`;

const TabNavButtonMobile = styled(TabNavButton)`
  align-self: center;
  text-align: center;
  width: 100%;
  padding: ${cssSpacing("xs")} 0;

  @media (min-width: ${mobileBreakPointCSS}) {
    display: none;
  }
`;

const TabNavPanel = styled.div`
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  transition: height 0.25s ease-in-out;

  gap: ${(props) => props.theme.spacing.xl};
  margin-top: ${cssSpacing("m")};
  padding: 0 ${(props) => `calc(${props.theme.spacing.xxl} / 3)`};

  > div {
    flex: none;
    width: 100%;
    box-sizing: border-box;
    scroll-snap-align: center;
    scroll-snap-stop: always;
  }

  > div:first-child {
    scroll-snap-align: end;
  }
  > div:last-child {
    scroll-snap-align: start;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-top: ${cssSpacing("xl")};
    overflow-x: hidden;
    padding: 0;
    height: auto;
    align-items: stretch;

    > div {
      width: 100%;
      padding: ${cssSpacing(0, 0, 0, "s")};
      scroll-snap-align: start !important;
    }
  }
`;

const TabNavStyledTab = styled.div<
  Pick<TabNavTabProps, "noBorderRadius"> & { $active: boolean }
>`
  display: flex;
  gap: ${(props) => props.theme.spacing.m};
  flex-direction: column;

  .rich-text {
    max-width: 600px;
    flex: 1;

    > :first-child {
      margin-top: 0;
    }
  }

  img {
    object-fit: cover;
  }

  .img {
    display: flex;
    justify-content: center;
    width: auto;
    height: 240px;
    max-width: 100%;
    img {
      height: auto;
      width: 100%;
      ${(props) =>
        props.noBorderRadius
          ? css`
              object-fit: contain;
              max-height: 250px;
            `
          : css`
              border-radius: ${(props) => props.theme.borderRadius};
              height: 100%;
              width: 100%;
            `};
    }
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    display: ${(props) => (props.$active ? "flex" : "none")};
    flex-direction: row;
    gap: ${(props) => props.theme.spacing.l}px;
    justify-content: center;
    height: 100%;

    .img {
      order: 1;
      width: 530px;
      height: auto;
    }
  }
`;

const Swipe = styled.button`
  background: #fff;
  border: 0;
  width: 50px;
  position: sticky;
  right: 0;
  top: 50%;
  padding: ${(props) => props.theme.spacing.s}px;
  border: 2px solid var(--swatch1);
  border-right: 0;
  background-color: #fff;
  color: var(--swatch2);
  border-radius: ${(props) => props.theme.borderRadius} 0 0
    ${(props) => props.theme.borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 6px rgba(24, 24, 24, 0.25);
  cursor: pointer;
  margin-left: auto;

  @media (min-width: ${mobileBreakPointCSS}) {
    display: none;
  }

  img {
    width: 30px;
  }
`;
